/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */

'use strict';
import { Injectable, NgZone } from '@angular/core';
import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import { throttle } from 'lodash-es';
import { Feature, ImageWrapper, Map as OlMap, Overlay as OlOverlay, View } from 'ol';

import Collection from 'ol/Collection';
import { Control, MousePosition, ScaleLine, Zoom } from 'ol/control';
import { Coordinate, format } from 'ol/coordinate';
import BaseEvent from 'ol/events/Event';
import { getWidth } from 'ol/extent';
import { GeoJSON } from 'ol/format';
import { Type } from 'ol/geom/Geometry';
import { Interaction } from 'ol/interaction';
import BaseLayer from 'ol/layer/Base';
import { Options as ImageOptions } from 'ol/layer/BaseImage';
import { Options as VectorOptions } from 'ol/layer/BaseVector';
import ImageLayer from 'ol/layer/Image';
import VectorLayer from 'ol/layer/Vector';
import { bbox } from 'ol/loadingstrategy';
import { ObjectEvent } from 'ol/Object';
import { get as getProj, toLonLat } from 'ol/proj';
import { register } from 'ol/proj/proj4';
import Projection from 'ol/proj/Projection';
import { ImageWMS, Vector as VectorSource } from 'ol/source';
import ImageSource from 'ol/source/Image';
import { StyleFunction } from 'ol/style/Style';

import proj4 from 'proj4';
import {
    asyncScheduler,
    BehaviorSubject,
    mergeMap,
    Observable,
    of,
    scheduled,
    Subject,
    switchMap,
    tap,
    withLatestFrom
} from 'rxjs';
import { catchError, filter, map, take } from 'rxjs/operators';
import { RedlineVisibilityChanged } from '../../../components/redline/state/redline.actions';
import { RedlineClassConfiguration, RedlineLayerConfiguration } from '../../../components/redline/state/redline.model';
import { RedlineState } from '../../../components/redline/state/redline.state';
import { AppConfigState } from '../../../core/state/appConfig/app-config.state';
import { GeometryCategory } from '../../../types/Map';
import { StyleConfiguration } from '../../../types/Style';
import { LoggerService } from '../../shared/services/logger.service';
import { RestService } from '../../shared/services/rest.service';
import { GoogleLayersControl } from '../components/GoogleLayersControl';
import { MapCreated, SetGoogleMapType, SetGoogleMaxImageryZoom, SetMapCenter, SetMapZoom } from '../state/active-map.actions';
import { ActiveMapModel } from '../state/active-map.model';
import { ActiveMapState } from '../state/active-map.state';
import { LayerStateChange, MapConfigLoaded } from '../state/map-config.actions';
import { GoogleMapService } from './googleMap.service';

import { MapConfigService } from './map-config.service';

@Injectable()
export class MapService {
    public static readonly MAX_ZOOM = 28;
    public googleMapType: google.maps.MapTypeId | string;
    public mapAvailable$: Observable<boolean>;

    private _layerObjects: Map<string, ImageLayer<ImageSource> | VectorLayer<VectorSource>>;
    private _map: OlMap;
    private _projection: Projection;
    private _mapAvailable = new BehaviorSubject<boolean>(false);
    private wmsLoaderSubject$ = new Subject<{ image: any, src: string }>();
    private setZoomStateThrottled: (number, Array) => void;
    private setCenterStateThrottled: (Coordinate) => void;

    constructor(
        private logger: LoggerService,
        private rest: RestService,
        private mapConfig: MapConfigService,
        private actions$: Actions,
        private store: Store,
        private googleMapService: GoogleMapService,
        private ngZone: NgZone
    ) {
        this.mapAvailable$ = this._mapAvailable.asObservable();

        this._layerObjects = new Map();

        proj4.defs(
            'EPSG:3857',
            '+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 ' +
                '+x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +wktext  +no_defs'
        );

        register(proj4);

        const onMapCreated$ = this.store
            .select((state) => state.map.created)
            .pipe(
                catchError((err) => {
                    this.logger.error('map.service.ts:102', err);
                    return of(false);
                }),
                filter((b) => b),
                take(1)
            );

        this.actions$
            .pipe(
                ofActionSuccessful(MapConfigLoaded),
                switchMap(() => onMapCreated$),
                tap(() => this.initializeWMSLoader())
            )
            .subscribe({
                next: () => this.map.getView().fit(this.mapConfig.initialExtent),
                error: (err) => this.logger.error(err),
            });

        this.actions$.pipe(ofActionSuccessful(LayerStateChange)).subscribe(() => {
            const layers = this.mapConfig.getVisibleLayerList(this.currentScale);
            const layerNames = layers.map((l) => l.name);
            this.refreshImageWMSLayers('layers', layerNames);
        });

        this.actions$
            .pipe(
                ofActionSuccessful(RedlineVisibilityChanged),
                switchMap(() => this.store.select(RedlineState.viewable))
            )
            .subscribe((viewable) => {
                if (viewable) {
                    viewable.forEach((item) => {
                        const layer = this.getVectorLayer(item.name, null, false);
                        if (layer) {
                            layer.setVisible(item.status);
                            layer.getSource().refresh();
                        }
                    });
                }
            });

        // make the projection for EPSG:2163 available (used in buffer operation)
        proj4.defs(
            'EPSG:2163',
            '+proj=laea +lat_0=45 +lon_0=-100 +x_0=0 +y_0=0 +a=6370997 +b=6370997 +units=m' + ' +no_defs'
        );

        register(proj4);

        this.actions$.pipe(ofActionSuccessful(SetGoogleMapType)).subscribe((action: SetGoogleMapType) => {
            this.setGoogleMapType(action.payload);
        });

        this.actions$.pipe(ofActionSuccessful(SetMapZoom, SetGoogleMaxImageryZoom)).subscribe(() => {
            const zoom = this.store.selectSnapshot(ActiveMapState.zoom);
            this.updateGoogleMapControls(zoom);
        });
    }

    public initMap() {
        this.destroyMap();
        this.createMap();
    }

    public destroyMap(): void {
        if (this._map) {
            const controls = this._map.getControls();
            const controlArray = controls.getArray();
            controls.clear();
            for (let i = controlArray.length; i > 0; --i) {
                delete controlArray[i];
            }
            this._map.getInteractions().clear();
            this._map.setTarget(null);
            this._map = null;
        }

        this._layerObjects.clear();
        this._mapAvailable.next(false);
    }

    public createMap() {
        scheduled(of(true), asyncScheduler)
            .pipe(
                mergeMap(() => this.store.select(ActiveMapState.state)),
                filter((state: ActiveMapModel) => state.initialized),
                take(1)
            )
            .subscribe((mapState: ActiveMapModel) => {
                proj4.defs(this.mapConfig.crs, this.mapConfig.projection);
                register(proj4);

                this._projection = this.mapConfig.crs ? getProj(this.mapConfig.crs) : getProj('EPSG:3857');
                this._projection.setGetPointResolution((resolution) => resolution);
                const view = this.createView(this._projection, mapState.center, MapService.MAX_ZOOM, mapState.zoom);

                this.ngZone.runOutsideAngular(() => {
                    this._map = new OlMap({
                        controls: this.getControls(),
                        interactions: new Collection<Interaction>(),
                        layers: null, // layers are set next
                        target: 'map',
                        view,
                    });
                });
                const layerNames = [];
                const layerConfigs = this.mapConfig
                    .getVisibleLayerList(this.currentScale)
                    .sort((a, b) => a.displayOrder - b.displayOrder);
                for (const layerConfig of layerConfigs) {
                    layerNames.push(layerConfig.name);
                }

                if (this.mapConfig.getBaseLayer().type === 'google') {
                    // this.configureGoogleMapsLayer();
                } else {
                    this.addImageLayer('base', [layerNames.shift()], true);
                }
                this.addImageLayer('layers', layerNames, false);

                this._mapAvailable.next(true);
                this.store.dispatch(new MapCreated());
            });
    }

    public getControls(): Control[] {
        return [
            new Zoom(),
            // new ol.control.ZoomSlider(),
            new ScaleLine({ units: 'us' }),
            new MousePosition({
                coordinateFormat: (coordinate) => format(coordinate, '{y}, {x}', 6),
                projection: getProj('EPSG:4326'),
                placeholder: '&nbsp;',
            }),
        ];
    }

    public createImageWmsSource(layerNames, isBaseLayer) {
        return new ImageWMS({
            imageLoadFunction: (image, src) => {
                this.imageWMSLoader(image, src);
            },
            params: {
                layers: layerNames.join(','),
                transparent: !isBaseLayer,
            },
            projection: this._projection,
            url: `map/ows/${this.mapConfig.mapName}`,
        });
    }

    public refreshImageWMSLayers(layerKey, layerNames) {
        const layer = this._layerObjects.get(layerKey);
        if (layerNames.length === 0) {
            layer.setVisible(false);
            return;
        }
        layer.setVisible(true);
        const source: ImageWMS = layer.getSource() as ImageWMS;
        const params: any = this.getSourceParams(source);
        params.layers = layerNames.join(',');
        // these two lines are a start at making different styles available from mapserver (light/dark)
        // const themeVariant = this.store.selectSnapshot(ThemeState.variant);
        // params.STYLES = layerNames.map(() => themeVariant).join(',');
        params.time = Date.now();
        source.updateParams(params);
        source.changed();
    }

    /**
     * @param targetElement {string}
     */
    public createPrintMap(targetElement): OlMap {
        const currentView = this.map.getView();
        const newView = new View({
            center: currentView.getCenter(),
            projection: currentView.getProjection(),
            resolution: currentView.getResolution(),
            resolutions: this.getResolutions(),
        });
        const newLayers = [];
        this.map.getLayers().forEach((layer: BaseLayer) => {
            if (layer instanceof ImageLayer) {
                const source = layer.getSource() as ImageWMS;
                const params: any = this.getSourceParams(source);
                const isBaseLayer = !params.transparent;
                const layers = params.layers;
                const newSource: ImageWMS = this.createImageWmsSource(layers.split(','), isBaseLayer);
                newSource.set('ratio', 1);
                const layerOptions: ImageOptions<ImageSource> = {
                    extent: layer.getExtent(),
                    maxResolution: layer.getMaxResolution(),
                    minResolution: layer.getMinResolution(),
                    opacity: layer.getOpacity(),
                    source: newSource,
                    visible: layer.getVisible(),
                };
                const newLayer = new ImageLayer(layerOptions);
                newLayers.push(newLayer);
            } else if (layer instanceof VectorLayer) {
                // let newSource = new ol.source.Vector({
                //     features: source.getFeatures(),
                //     format: source.getFormat()
                // });
                const layerOptions: VectorOptions<VectorSource> = {
                    extent: layer.getExtent(),
                    maxResolution: layer.getMaxResolution(),
                    minResolution: layer.getMinResolution(),
                    opacity: layer.getOpacity(),
                    source: layer.getSource() as VectorSource,
                    style: layer.getStyleFunction() || layer.getStyle(),
                    visible: layer.getVisible(),
                };
                const newLayer = new VectorLayer(layerOptions);
                newLayers.push(newLayer);
            }
        });

        const overlays = new Collection<OlOverlay>();
        this.map.getOverlays().forEach((overlay: OlOverlay) => {
            overlays.push(
                new OlOverlay({
                    id: overlay.getId(),
                    element: overlay.getElement().cloneNode(true) as HTMLElement,
                    offset: overlay.getOffset(),
                    position: overlay.getPosition(),
                    positioning: overlay.getPositioning(),
                })
            );
        });

        return new OlMap({
            controls: [],
            layers: newLayers,
            overlays,
            target: targetElement,
            view: newView,
        });
    }

    public hasLayer(layerName: string) {
        return this._layerObjects.has(layerName);
    }

    public getLayer(layerName: string) {
        return this._layerObjects.get(layerName);
    }

    public setLayer(layerName: string, layerObject: ImageLayer<ImageSource> | VectorLayer<VectorSource>) {
        this._layerObjects.set(layerName, layerObject);
    }

    /**
     * Creates or retrieves a VectorLayer
     * @param key string The key used to reference the layer
     * @param source VectorSource|null Optional source used when creating the layer. Ignored if layer exists
     * @param create boolean Whether to allow creation of the layer if it doesn't exist
     */
    getVectorLayer(key: string, source: VectorSource = null, create = true): VectorLayer<VectorSource> {
        let returnLayer;
        if (this._layerObjects.has(key)) {
            returnLayer = this._layerObjects.get(key) as VectorLayer<VectorSource>;
        } else if (create) {
            const layerZIndices = {
                searchFeatures: 1,
                buffer: 5,
                isolation: 10,
                markup: 15,
                selection: 20,
                location: 25,
            };
            const vectorLayer = new VectorLayer({
                renderOrder: null,
                source: source || new VectorSource(),
            });
            vectorLayer.set('name', key);
            this._layerObjects.set(key, vectorLayer);
            const layerZIndex = layerZIndices[key] || -1;
            if (layerZIndex !== -1) {
                vectorLayer.setZIndex(layerZIndex);
            }
            this.map.addLayer(vectorLayer);
            returnLayer = vectorLayer;
        }
        return returnLayer;
    }

    /**
     * @param layerConfigs {Array}
     * @param loaderFunction {Function}
     */
    public createRedlineLayers(layerConfigs, loaderFunction) {
        layerConfigs.forEach((config) => {
            this.createRedlineLayer(config.name, config, loaderFunction);
        });
    }

    public createMeasureLayer(): VectorLayer<VectorSource> {
        // get measure layer
        if (!this._layerObjects.has('measure')) {
            const measureLayer = new VectorLayer({
                renderOrder: null,
                source: new VectorSource(),
            });
            measureLayer.set('layerName', 'measure');
            this._layerObjects.set('measure', measureLayer);
            this.map.addLayer(measureLayer);
        }
        return this._layerObjects.get('measure') as VectorLayer<VectorSource>;
    }

    public getMeasureLayer(): VectorLayer<VectorSource> | null {
        if (this._layerObjects.has('measure')) {
            return this._layerObjects.get('measure') as VectorLayer<VectorSource>;
        } else {
            return null;
        }
    }

    public createPolygonSelectLayer(): VectorLayer<VectorSource> {
        if (!this._layerObjects.has('polygon-select')) {
            const measureLayer = new VectorLayer<VectorSource>({
                renderOrder: null,
                source: new VectorSource(),
            });
            this._layerObjects.set('polygon-select', measureLayer);
            this.map.addLayer(measureLayer);
        }
        return this._layerObjects.get('polygon-select') as VectorLayer<VectorSource>;
    }

    public getPolygonSelectLayer(): VectorLayer<VectorSource> | null {
        if (this._layerObjects.has('polygon-select')) {
            return this._layerObjects.get('polygon-select') as VectorLayer<VectorSource>;
        } else {
            return null;
        }
    }

    public getGeometryCategoryByFeature(feature: Feature): GeometryCategory {
        const geometryType: Type = feature.getGeometry().getType();
        return this.getGeometryCategoryByTypeName(geometryType.toString().toLowerCase());
    }

    public getGeometryCategoryByTypeName(typeName: string): GeometryCategory {
        switch (typeName.toLowerCase()) {
            case 'point':
            case 'multipoint':
                return GeometryCategory.Point;
            case 'line':
            case 'linestring':
            case 'multilinestring':
            case 'linearring':
                return GeometryCategory.Line;
            case 'polygon':
            case 'multipolygon':
            case 'circle':
            case 'geometrycollection':
                return GeometryCategory.Polygon;
        }
    }

    public setCursor(cursor: string): void {
        const classes = this.map.getTargetElement().classList;
        const available = ['cursor-default', 'cursor-move', 'cursor-crosshair', 'cursor-zoom-in', 'cursor-zoom-out'];
        let current = null;
        for (let i = 0; i < classes.length; i++) {
            if (available.indexOf(classes[i]) !== -1) {
                current = classes[i];
            }
        }
        if (current) {
            classes.remove(current);
        }
        classes.add(`cursor-${cursor}`);
    }

    public getRedlineStyleFunction(config: RedlineLayerConfiguration): StyleFunction {
        const classItem = config.classItem;
        return (feature, resolution) => {
            let item = feature.get(classItem) || 'DEFAULT';
            if (Object.getOwnPropertyNames(config.classes).indexOf(item) === -1) {
                item = 'DEFAULT';
            }
            const classConfig: RedlineClassConfiguration = config.classes[item];
            if (classConfig.styles) {
                const classStyles: StyleConfiguration[] = config.classes[item].styles;
                return this.mapConfig.getStyles(
                    `redline_${config.name}_${item}`,
                    this.getGeometryCategoryByTypeName(config.type),
                    classStyles
                );
            }
        };
    }

    private getSourceParams(source: ImageWMS): Object {
        const params = source.getParams();
        const normalized = {};
        Object.keys(params).forEach((i) => (normalized[i.toLowerCase()] = params[i]));
        return normalized;
    }

    private setGoogleMapType(type: string): void {
        const baseLayerConfig = this.store.selectSnapshot(AppConfigState.baseLayer);
        const maxImageryZoom = this.store.selectSnapshot(ActiveMapState.maxImageryZoom);
        const layerType = baseLayerConfig.layerTypes.find((x) => x.type === type);
        const currentView = this.map.getView();
        let newView: View;
        if (layerType.isImagery) {
            const center = new google.maps.LatLng(this.centerLatLon[1], this.centerLatLon[0]);
            newView = this.createView(this._projection, currentView.getCenter(), maxImageryZoom, currentView.getZoom());
        } else {
            newView = this.createView(
                this._projection,
                currentView.getCenter(),
                MapService.MAX_ZOOM,
                currentView.getZoom()
            );
        }
        this.map.setView(newView);
    }

    private updateGoogleMapControls(zoom) {
        const maxZoom = this.store.selectSnapshot(ActiveMapState.maxImageryZoom);
        const googleLayerControls: Control[] = (this.map.getControls() as Collection<Control>)
            .getArray()
            .filter((item) => item instanceof GoogleLayersControl);

        if (zoom > maxZoom) {
            googleLayerControls.forEach((control) => {
                (control as GoogleLayersControl).disableImagery();
            });
        } else {
            googleLayerControls.forEach((control) => {
                (control as GoogleLayersControl).enableImagery();
            });
        }
    }

    private createView(projection: Projection, center: Coordinate, maxZoom: number, zoom = 15) {
        const newView = new View({
            center,

            // minZoom (default is 0)
            maxZoom,

            // default is EPSG:3857
            projection,

            // extent in this context limits range of the view's center
            // extent: this.mapConfig.extent,

            // calculate via maxZoom
            // resolutions: this.getResolutions(),
            constrainResolution: true,
            smoothResolutionConstraint: false,

            // easing: linear,

            // dummy value - we change this immediately (below)
            zoom,

            zoomFactor: 2,
        });

        const baseLayerConfig = this.store.selectSnapshot(AppConfigState.baseLayer);
        if ('google' === baseLayerConfig.type) {
            newView.on('change:resolution', (evt) => {
                this.onResolutionChange(evt);
            });

            newView.on('change:center', (evt) => {
                this.onCenterChange(evt);
            });
        }
        return newView;
    }

    private initializeWMSLoader() {
        this.wmsLoaderSubject$.pipe(
            map(req => [req.image, req.src]),
            map(([image, src]: [any, string]) => {
                const urlParts = src.split('?');
                const base = urlParts[0];
                let query = '';
                const params = {};
                if (urlParts.length > 1) {
                    query = urlParts[1];
                    const queryParts = query.split('&');
                    queryParts.forEach((item) => {
                        const [k, v] = item.split('=');
                        params[k] = v;
                    });
                }
                return {
                    image,
                    base,
                    params
                }
            }),
            switchMap((req) => {
                return this.rest.apiGet<string>(req.base, req.params, { responseType: 'text' }).pipe(
                    map(result => ({data: result, image: req.image})),
                    catchError((err, caught) => {
                        this.logger.error(err);
                        return of(null);
                    }),
                )
            }),
        ).subscribe(result => {
            if (result?.data && result?.image) {
                result.image.getImage().src = result.data;
            }
        });
    }

    private imageWMSLoader(image, src) {
        this.wmsLoaderSubject$.next({ image, src });
    }

    private addImageLayer(layerKey, layerNames, isBaseLayer) {
        const layer = new ImageLayer({
            source: this.createImageWmsSource(layerNames, isBaseLayer),
        });
        if (layerNames.length === 0) {
            layer.setVisible(false);
        }

        this._map.addLayer(layer);
        this._layerObjects.set(layerKey, layer);
    }

    private onResolutionChange(event: BaseEvent) {
        const zoom = this.map.getView().getZoom();
        this.googleMapService.setZoom(zoom);
        const layers = this.mapConfig.getVisibleLayerList(this.currentScale);
        if (!this.setZoomStateThrottled) {
            this.setZoomStateThrottled = throttle((zoomValue: number, layerList) => {
                this.store.dispatch(new SetMapZoom({ zoom: zoomValue, scale: this.currentScale }));
                this.refreshImageWMSLayers(
                    'layers',
                    layerList.map((item) => item.name)
                );
            }, 100);
        }
        this.setZoomStateThrottled(zoom, layers);
    }

    private onCenterChange(event: ObjectEvent) {
        const center = event.target.get(event.key);
        this.googleMapService.setCenter(center);
        if (!this.setCenterStateThrottled) {
            this.setCenterStateThrottled = throttle((centerValue: Coordinate) => {
                this.store.dispatch(new SetMapCenter(centerValue));
            }, 200);
        }
        this.setCenterStateThrottled(center);
    }

    private createRedlineLayer(
        name: string,
        config: RedlineLayerConfiguration,
        loaderFunction: (name: string) => any
    ): void {
        if (!this._layerObjects.has(name)) {
            if (!this.map) {
                return;
            }
            const redlineStyleFn = this.getRedlineStyleFunction(config);
            const redlineLayer = new VectorLayer({
                maxResolution: this.mapConfig.getResFromScaleDenom(config.maxScale, this.mapConfig.units, 96),
                minResolution: this.mapConfig.getResFromScaleDenom(config.minScale, this.mapConfig.units, 96),
                renderOrder: null,
                source: new VectorSource({
                    format: new GeoJSON({ geometryName: 'geometry' }),
                    loader: loaderFunction(name),
                    strategy: bbox,
                    useSpatialIndex: true,
                }),
                style: redlineStyleFn,
                visible: config.status,
            });
            this._layerObjects.set(name, redlineLayer);
            redlineLayer.set('name', name);
            this.map.addLayer(redlineLayer);
        }
    }

    /**
     * Calculate zoom / resolutions for Web Mercator projection (used by Google maps)
     * @returns {Array}
     */
    private getResolutions() {
        const projection = getProj('EPSG:3857');
        const projectionExtent = projection.getExtent();
        const size = getWidth(projectionExtent) / 256;
        const resolutions = new Array(MapService.MAX_ZOOM + 1);
        const zz = resolutions.length;
        for (let z = 0; z < zz; ++z) {
            // generate resolutions and matrixIds arrays for this projection
            resolutions[z] = size / Math.pow(2, z);
        }
        return resolutions;
    }

    // region Accessors
    get currentScale() {
        if (this._map) {
            const resolution = this._map.getView().getResolution();
            return this.mapConfig.getScaleDenominationForResolution(resolution, this.mapConfig.units, 96);
        } else {
            return 1000000000;
        }
    }

    get currentResolution() {
        if (this._map) {
            return this._map.getView().getResolution();
        }
    }

    get zoom() {
        return this._map.getView().getZoom();
    }

    get map(): OlMap {
        return this._map;
    }

    get center(): Coordinate {
        return this._map.getView().getCenter();
    }

    get centerLatLon(): Coordinate {
        return toLonLat(this._map.getView().getCenter());
    }

    get projection(): Projection {
        return this._projection;
    }

    // endregion
}

export class MapServiceFeatureSelectError extends Error {}
